import type { PropsWithChildren } from 'react';
import React from 'react';

import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import { concatRoutes, formatRoute, parser } from '/imports/modules/Routing';
import { Authorise } from '/imports/ui/components/Authorise';
import { useSetPreferredEntry } from '/imports/ui/hooks/useSetPreferredEntry';
import type { AdminNavigationProps } from '/imports/ui/pages/admin/AdminNavigation';
import {
  adminAnalyticsRoute,
  adminAnalyticsSectionRoute,
  adminCoursesRoute,
  adminEnvRoute,
  adminNotificationsRoute,
  adminRoute,
  adminRunnersRoute,
  adminSettingsRoute,
  adminUsersLink,
  adminUsersRoute,
} from './routes';

const AdminNavigation = React.lazy(() => import('./AdminNavigation'));
const Analytics = React.lazy(() => import('./Analytics'));
const Courses = React.lazy(() => import('./Courses'));
const EnvironmentOverview = React.lazy(() => import('./enviroment/EnvironmentOverview'));
const GlobalNotification = React.lazy(() => import('./GlobalNotification'));
const Runners = React.lazy(() => import('./Runners'));
const Settings = React.lazy(() => import('./settings/SettingsOverview'));
const Users = React.lazy(() => import('./users'));

const AdminPageWrapper = ({ children }: PropsWithChildren) => {
  useSetPreferredEntry({ path: formatRoute(adminRoute)({}) });
  return (
    <Authorise allow={[]} deny={['lti']} redirectToHomepage={true}>
      {children}
    </Authorise>
  );
};

const AdminPage = ({ page, children }: AdminNavigationProps) => (
  <AdminPageWrapper>
    <AdminNavigation page={page}>{children}</AdminNavigation>
  </AdminPageWrapper>
);

export const adminRouter = concatRoutes([
  pipe(
    adminAnalyticsSectionRoute.parser,
    parser.map(({ section }) => (
      <AdminPage page="analytics">
        <Analytics section={section} />
      </AdminPage>
    )),
  ),
  pipe(
    adminAnalyticsRoute.parser,
    parser.map(() => (
      <AdminPage page="analytics">
        <Navigate to={formatRoute(adminAnalyticsSectionRoute)({ section: 'status' })} />
      </AdminPage>
    )),
  ),
  pipe(
    adminCoursesRoute.parser,
    parser.map(({ section }) => (
      <AdminPage page="courses">
        <Courses section={section} />
      </AdminPage>
    )),
  ),
  pipe(
    adminEnvRoute.parser,
    parser.map(() => (
      <AdminPage page="env">
        <EnvironmentOverview />
      </AdminPage>
    )),
  ),
  pipe(
    adminNotificationsRoute.parser,
    parser.map(() => (
      <AdminPage page="notifications">
        <GlobalNotification />
      </AdminPage>
    )),
  ),
  pipe(
    adminRunnersRoute.parser,
    parser.map(({ section }) => (
      <AdminPage page="runners">
        <Runners section={section} />
      </AdminPage>
    )),
  ),
  pipe(
    adminSettingsRoute.parser,
    parser.map(() => (
      <AdminPage page="settings">
        <Settings />
      </AdminPage>
    )),
  ),
  pipe(
    adminUsersRoute.parser,
    parser.map(({ section }) => (
      <AdminPage page="users">
        <Users section={section} />
      </AdminPage>
    )),
  ),
  pipe(
    adminRoute.parser,
    parser.map(() => (
      <AdminPageWrapper>
        <Navigate to={adminUsersLink({ section: 'online' })} />
      </AdminPageWrapper>
    )),
  ),
]);
